import { cx } from '../../lib/cx';

export interface UnderlineTab<T extends string> {
  value: T;
  label: string;
  count?: number;
  /** Shown as in the design but not usable yet; choosing it calls `onUnavailable`. */
  unavailable?: boolean;
}

/**
 * Figma "Views" tabs (A03.1, A04): text with a count, a 2px underline under the chosen one, a hairline under the row.
 * Tabs the product cannot back yet stay visible per the design and are inert (`unavailable`).
 */
export function UnderlineTabs<T extends string>({
  tabs,
  value,
  onChange,
  onUnavailable,
  'aria-label': ariaLabel,
  className,
}: {
  tabs: UnderlineTab<T>[];
  value: T;
  onChange: (value: T) => void;
  onUnavailable?: (tab: UnderlineTab<T>) => void;
  'aria-label': string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx('flex gap-(--mbm-space-6) border border-border-line', className)}
    >
      {tabs.map((tab) => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-disabled={tab.unavailable || undefined}
            onClick={() => (tab.unavailable ? onUnavailable?.(tab) : onChange(tab.value))}
            className={cx(
              'flex flex-col gap-(--mbm-space-2-5) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
              tab.unavailable && 'cursor-not-allowed',
            )}
          >
            <span className="flex items-center gap-(--mbm-space-1-5) pt-(--mbm-space-1)">
              <span className={cx('text-label-medium', selected ? 'text-text-primary' : 'text-text-secondary')}>
                {tab.label}
              </span>
              {tab.count !== undefined ? (
                <span className="text-body-xsmall text-text-tertiary">{tab.count}</span>
              ) : null}
            </span>
            <span
              aria-hidden
              className={cx('h-(--mbm-space-0-5) w-full', selected ? 'bg-action-primary' : 'bg-transparent')}
            />
          </button>
        );
      })}
    </div>
  );
}
